import { useQuery } from '@tanstack/react-query';
import { fetchDealers, fetchDealerGroupMembers } from '../services/dealers';
import type { DealerSearchParams, DealerSearchResponse, DealerType } from '../types/dealers.types';

export const dealerKeys = {
    all: ['dealers'] as const,
    lists: () => [...dealerKeys.all, 'list'] as const,
    list: (filters: DealerSearchParams) => [...dealerKeys.lists(), filters] as const,
    detail: (id: string) => [...dealerKeys.all, 'detail', id] as const,
    liveList: (filters: DealerSearchParams) => [...dealerKeys.all, 'live-list', filters] as const,
    byIds: (ids: string[]) => [...dealerKeys.all, 'by-ids', ids] as const,
    agreementSummaries: (ids: string[]) => [...dealerKeys.all, 'agreement-summaries', ids] as const,
    bankAccounts: (dealerId: string) => [...dealerKeys.all, dealerId, 'bank-accounts'] as const,
    contacts: (dealerId: string) => [...dealerKeys.all, dealerId, 'contacts'] as const,
    margins: (dealerId: string) => [...dealerKeys.all, dealerId, 'margins'] as const,
    groupMembers: (dealerId: string) => [...dealerKeys.all, dealerId, 'group-members'] as const,
    sageMappings: (dealerId: string) => [...dealerKeys.all, dealerId, 'sage-mappings'] as const,
    fundStatement: (dealerId: string, page: number, perPage: number) =>
        [...dealerKeys.all, dealerId, 'fund-statement', page, perPage] as const,
    fundStatementPeriod: (dealerId: string, periodKey: string, openDay: string | null) =>
        [...dealerKeys.all, dealerId, 'fund-statement-period', periodKey, openDay] as const,
    fundStatementPeriodPair: (
        sellingDealerId: string,
        productId: string,
        periodKey: string,
        openDay: string | null,
    ) => [...dealerKeys.all, sellingDealerId, 'fund-statement-period', 'pair', productId, periodKey, openDay] as const,
    fundStatementPeriodRows: (
        sellingDealerId: string,
        productId: string,
        periodKey: string,
        openDay: string | null,
        trancode: string,
        page: number,
    ) => [
        ...dealerKeys.fundStatementPeriodPair(sellingDealerId, productId, periodKey, openDay),
        'rows',
        trancode,
        page,
    ] as const,
    productNames: () => [...dealerKeys.all, 'product-names'] as const,
};

export const useDealerGroupMembers = (dealerId: string) => {
    return useQuery({
        queryKey: dealerKeys.groupMembers(dealerId),
        queryFn: () => fetchDealerGroupMembers(dealerId),
        enabled: !!dealerId,
    });
};

export const useDealersQuery = (
    params: DealerSearchParams = {},
    options: { enabled?: boolean } = {},
) => {
    return useQuery<DealerSearchResponse>({
        queryKey: dealerKeys.list(params),
        queryFn: () => fetchDealers(params),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        enabled: options.enabled ?? true,
    });
};

export const useLiveDealersQuery = (
    params: DealerSearchParams = {},
    options: { enabled?: boolean } = {},
) => {
    return useQuery<DealerSearchResponse>({
        queryKey: dealerKeys.liveList(params),
        queryFn: () => fetchDealers(params),
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always',
        enabled: options.enabled ?? true,
    });
};

/**
 * Candidate owning-parent dealers for a given (child) dealer type, filtered
 * to active dealers of the matrix-allowed parent type. Used by
 * AllowedParentSelect to drive the owning-parent picker in DealerForm.
 */
export const useAllowedParentDealers = (
    parentType: DealerType | null,
    search: string,
    enabled: boolean,
) => useDealersQuery(
    // Live = any non-retired dealer (active family + pending), not just literal
    // 'active' — a retired (inactive) dealer cannot be an owning parent.
    { type: parentType ?? undefined, is_live: true, search: search || undefined, per_page: 20 },
    { enabled: enabled && !!parentType },
);
